import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import templateService, { Template } from '@/services/templateService';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import {
  InchstoneHierarchy,
  Inchstone
} from '@/components/inchstones/InchstoneHierarchy';

// Internal structure for template nodes (uses weeks)
interface ProjectStructureNode {
  id: string;
  title: string;
  description?: string;
  start_week?: number;
  end_week?: number;
  children: ProjectStructureNode[];
}

// Base date for calculating relative dates from weeks (week 1 = this date)
const BASE_DATE = new Date('2025-01-01');

const generateNodeId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const sanitizeWeekNumber = (value?: number, fallback = 1) => {
  if (value === undefined || value === null || Number.isNaN(value) || value < 1) {
    return fallback;
  }
  return Math.floor(value);
};

// Convert week number to date string (for Inchstone interface)
const weekToDate = (week: number): string => {
  const date = new Date(BASE_DATE);
  date.setDate(date.getDate() + (week - 1) * 7);
  return date.toISOString().split('T')[0];
};

// Convert date string back to week number
const dateToWeek = (dateString: string): number => {
  const date = new Date(dateString);
  const diffTime = date.getTime() - BASE_DATE.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return Math.floor(diffDays / 7) + 1;
};

// Convert ProjectStructureNode to Inchstone format (for display)
const nodeToInchstone = (node: ProjectStructureNode, transformationId: string, userId: string, tenantId: string): Inchstone => {
  return {
    id: node.id,
    name: node.title,
    description: node.description || '',
    start_date: node.start_week ? weekToDate(node.start_week) : undefined,
    due_date: node.end_week ? weekToDate(node.end_week) : undefined,
    task_type: 'General',
    status: 'PENDING',
    priority: 'MEDIUM',
    parent_id: undefined, // Will be set by hierarchy structure
    transformation_id: transformationId,
    owner_user_id: userId,
    tenant_id: tenantId,
    sort_order: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    children: node.children.map(child => nodeToInchstone(child, transformationId, userId, tenantId))
  };
};

// Convert Inchstone back to ProjectStructureNode format (for saving)
const inchstoneToNode = (inchstone: Inchstone): ProjectStructureNode => {
  return {
    id: inchstone.id,
    title: inchstone.name,
    description: inchstone.description || '',
    start_week: inchstone.start_date ? dateToWeek(inchstone.start_date) : 1,
    end_week: inchstone.due_date ? dateToWeek(inchstone.due_date) : 1,
    children: (inchstone.children || []).map(child => inchstoneToNode(child))
  };
};


const createEmptyNode = (title?: string): ProjectStructureNode => ({
  id: generateNodeId(),
  title: title || '',
  description: '',
  start_week: 1,
  end_week: 1,
  children: []
});

const mapStoredNodesToState = (nodes: any[]): ProjectStructureNode[] =>
  nodes.map((node: any) => ({
    id: generateNodeId(),
    title: (node.title || node.name || '').trim(),
    description: node.description || '',
    start_week: sanitizeWeekNumber(node.start_week, 1),
    end_week: sanitizeWeekNumber(node.end_week ?? node.start_week, sanitizeWeekNumber(node.start_week, 1)),
    children: Array.isArray(node.children) ? mapStoredNodesToState(node.children) : []
  }));

const serializeNodes = (nodes: ProjectStructureNode[]): any[] =>
  nodes.map(node => ({
    title: node.title.trim(),
    description: node.description?.trim() || '',
    start_week: sanitizeWeekNumber(node.start_week, 1),
    end_week: sanitizeWeekNumber(node.end_week ?? node.start_week, sanitizeWeekNumber(node.start_week, 1)),
    children: node.children.length > 0 ? serializeNodes(node.children) : []
  }));

export const ProjectStructureTemplateBuilder: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editTemplateId = searchParams.get('edit');
  const isEditMode = Boolean(editTemplateId);

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [structure, setStructure] = useState<ProjectStructureNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalTemplate, setOriginalTemplate] = useState<Template | null>(null);
  
  // Edit modal state
  const [editingInchstone, setEditingInchstone] = useState<Inchstone | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    start_week: 1,
    end_week: 1
  });

  // Convert structure to inchstones for display
  const inchstones = useMemo(() => {
    if (!user?.id || !user?.tenantId) return [];
    const dummyTransformationId = 'template-builder';
    return structure.map(node => nodeToInchstone(node, dummyTransformationId, user.id, user.tenantId));
  }, [structure, user?.id, user?.tenantId]);

  useEffect(() => {
    if (editTemplateId) {
      loadTemplate(editTemplateId);
    } else {
      // Start with one empty node
      setStructure([createEmptyNode()]);
    }
  }, [editTemplateId]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const template = await templateService.getTemplate(templateId);
      if (template.template_type !== 'PROJECT_STRUCTURE') {
        toast.error(t('templates.errors.templateTypeNotSupported') || 'Unsupported template type');
        navigate('/templates');
        return;
      }
      setOriginalTemplate(template);
      setTemplateName(template.name || '');
      setTemplateDescription(template.description || '');
      const storedStructure = template.content?.structure?.project_structure || template.content?.project_structure;
      if (Array.isArray(storedStructure) && storedStructure.length > 0) {
        setStructure(mapStoredNodesToState(storedStructure));
      } else {
        setStructure([createEmptyNode(template.name)]);
      }
    } catch (err) {
      console.error('Error loading template', err);
      toast.error(t('templates.errors.failedToLoadTemplate') || 'Failed to load template');
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInchstone = (parentId?: string) => {
    const newNode = createEmptyNode();
    if (parentId) {
      // Add as child
      setStructure(prev => {
        const updateNode = (nodes: ProjectStructureNode[]): ProjectStructureNode[] => {
          return nodes.map(node => {
            if (node.id === parentId) {
              return { ...node, children: [...node.children, newNode] };
            }
            if (node.children.length > 0) {
              return { ...node, children: updateNode(node.children) };
            }
            return node;
          });
        };
        return updateNode(prev);
      });
    } else {
      // Add as root
      setStructure(prev => [...prev, newNode]);
    }
  };

  const handleEditInchstone = (inchstone: Inchstone) => {
    setEditingInchstone(inchstone);
    setEditFormData({
      name: inchstone.name,
      description: inchstone.description || '',
      start_week: inchstone.start_date ? dateToWeek(inchstone.start_date) : 1,
      end_week: inchstone.due_date ? dateToWeek(inchstone.due_date) : 1
    });
  };

  const handleSaveEdit = () => {
    if (!editingInchstone) return;

    setStructure(prev => {
      const updateNode = (nodes: ProjectStructureNode[]): ProjectStructureNode[] => {
        return nodes.map(node => {
          if (node.id === editingInchstone.id) {
            return {
              ...node,
              title: editFormData.name,
              description: editFormData.description,
              start_week: sanitizeWeekNumber(editFormData.start_week, 1),
              end_week: sanitizeWeekNumber(editFormData.end_week, editFormData.start_week)
            };
          }
          if (node.children.length > 0) {
            return { ...node, children: updateNode(node.children) };
          }
          return node;
        });
      };
      return updateNode(prev);
    });

    setEditingInchstone(null);
    setEditFormData({ name: '', description: '', start_week: 1, end_week: 1 });
  };

  const handleDeleteInchstone = (id: string) => {
    setStructure(prev => {
      const removeNode = (nodes: ProjectStructureNode[]): ProjectStructureNode[] => {
        return nodes
          .filter(node => node.id !== id)
          .map(node => ({
            ...node,
            children: node.children.length > 0 ? removeNode(node.children) : []
          }));
      };
      return removeNode(prev);
    });
  };

  const handleToggleStatus = (_id: string, _status: string) => {
    // Status toggle doesn't make sense for templates, but we'll keep the interface consistent
    // Just do nothing or could show a message
  };

  const handleReorder = (inchstoneId: string, newSortOrder: number, newParentId?: string) => {
    console.log('handleReorder called:', { inchstoneId, newSortOrder, newParentId });
    setStructure(prev => {
      // Find and extract the node to move
      let movedNode: ProjectStructureNode | null = null;

      const findAndRemove = (nodes: ProjectStructureNode[]): ProjectStructureNode[] => {
        const result: ProjectStructureNode[] = [];
        for (const node of nodes) {
          if (node.id === inchstoneId) {
            // Found the node to move - extract it (don't add to result)
            movedNode = { ...node, children: [...node.children] };
            console.log('Found node to move:', movedNode);
          } else {
            // Keep this node, but recursively process its children
            result.push({
              ...node,
              children: node.children.length > 0 ? findAndRemove(node.children) : []
            });
          }
        }
        return result;
      };

      const cleaned = findAndRemove(prev);

      // If node not found, return unchanged
      if (!movedNode) {
        console.warn('Node not found for reorder:', inchstoneId, 'Available IDs:', prev.map(n => n.id));
        return prev;
      }

      if (newParentId) {
        // Add as child of new parent
        const addAsChild = (nodes: ProjectStructureNode[]): ProjectStructureNode[] => {
          return nodes.map(node => {
            if (node.id === newParentId) {
              // Found the parent - add moved node as child
              console.log('Adding as child of:', node.id);
              return { ...node, children: [...node.children, movedNode!] };
            }
            if (node.children.length > 0) {
              return { ...node, children: addAsChild(node.children) };
            }
            return node;
          });
        };
        const result = addAsChild(cleaned);
        console.log('Reordered result (as child):', result);
        return result;
      } else {
        // Add as root at the specified sort order
        const result = [...cleaned];
        result.splice(newSortOrder, 0, movedNode);
        console.log('Reordered result (as root):', result);
        return result;
      }
    });
  };

  const hasStructure = useMemo(() => structure.length > 0 && structure.some(node => node.title.trim() !== ''), [structure]);

  const validateBeforeSave = (): boolean => {
    if (!templateName.trim()) {
      setError(t('templates.pleaseEnterTemplateName') || 'Please enter a template name');
      return false;
    }
    if (!hasStructure) {
      setError(t('templates.projectStructureNeedsNodes') || 'Add at least one inchstone');
      return false;
    }
    const missingTitle = structure.some(node => {
      const checkNode = (n: ProjectStructureNode): boolean => {
        if (n.title.trim() === '') return true;
        return n.children.some(child => checkNode(child));
      };
      return checkNode(node);
    });
    if (missingTitle) {
      setError(t('templates.projectStructureMissingTitles') || 'All inchstones need names');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!user?.tenantId || !user?.id) {
      toast.error(t('templates.pleaseLogInToSave') || 'Log in to save templates');
      return;
    }

    if (!validateBeforeSave()) {
      return;
    }

    try {
      setSaving(true);
      const serialized = serializeNodes(structure);
      const content = {
        template_metadata: {
          name: templateName.trim(),
          description: templateDescription.trim(),
          version: originalTemplate?.version || '1.0'
        },
        structure: {
          project_structure: serialized
        }
      };

      if (isEditMode && editTemplateId) {
        await templateService.updateTemplate(editTemplateId, {
          name: templateName.trim(),
          description: templateDescription.trim(),
          content,
          marketplace_visibility: originalTemplate?.marketplace_visibility || 'DRAFT'
        });
        toast.success(t('templates.templateUpdated') || 'Template updated');
      } else {
        await templateService.createTemplate({
          tenant_id: user.tenantId,
          name: templateName.trim(),
          description: templateDescription.trim(),
          template_type: 'PROJECT_STRUCTURE',
          content,
          marketplace_visibility: 'DRAFT',
          version: '1.0',
          created_by: user.id
        });
        toast.success(t('templates.templateCreated') || 'Template created');
      }
      navigate('/templates');
    } catch (err) {
      console.error('Error saving template', err);
      toast.error(t('templates.errors.failedToSaveTemplate') || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">{t('templates.loading') || 'Loading template...'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-2 sm:px-2 lg:px-2 py-2 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEditMode
                ? t('templates.editProjectStructureTemplate') || 'Edit Project Structure Template'
                : t('templates.projectStructureBuilderTitle') || 'Project Structure Template Builder'}
            </h1>
            <p className="text-xs text-gray-600">
              {t('templates.projectStructureBuilderDescription') ||
                'Model inchstone hierarchies with start/end weeks and reuse them across projects.'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="secondary" onClick={() => navigate('/templates')}>
              {t('templates.backToTemplates') || 'Back to Templates'}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? t('templates.saving') || 'Saving...'
                : t('templates.saveTemplate') || 'Save template'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <Card>
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('templates.templateName') || 'Template name'}
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('templates.templateNamePlaceholder') || 'Customer transformation cadence'}
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              {t('templates.description') || 'Description'}
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('templates.optionalDescription') || 'Optional description'}
                rows={3}
              />
            </label>
          </div>
        </Card>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('templates.projectStructure') || 'Project Structure'}
              </h2>
              <p className="text-sm text-gray-600">
                {t('templates.projectStructureHelperText') ||
                  'Add phases and nested inchstones with relative start/end weeks.'}
              </p>
            </div>
            <Button variant="outline" onClick={() => handleCreateInchstone()}>
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('templates.addPhase') || 'Add top-level phase'}
            </Button>
          </div>

          {inchstones.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500 mb-4">
                {t('templates.noProjectStructureNodes') || 'No inchstones defined yet.'}
              </p>
              <Button onClick={() => handleCreateInchstone()}>
                <PlusIcon className="h-4 w-4 mr-2" />
                {t('templates.addPhase') || 'Add top-level phase'}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <InchstoneHierarchy
                inchstones={inchstones}
                onEdit={handleEditInchstone}
                onDelete={handleDeleteInchstone}
                onToggleStatus={handleToggleStatus}
                onCreateChild={handleCreateInchstone}
                onReorder={handleReorder}
                hideStatusToggle={true}
                templateMode={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Inchstone Modal - Shows weeks instead of dates */}
      {editingInchstone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('templates.editInchstone') || 'Edit Inchstone'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.name') || 'Name'} *
                </label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.description') || 'Description'}
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.startWeek') || 'Start Week'} *
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={editFormData.start_week}
                    onChange={(e) => {
                      const week = Math.max(1, parseInt(e.target.value) || 1);
                      setEditFormData({
                        ...editFormData,
                        start_week: week,
                        end_week: Math.max(week, editFormData.end_week)
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('templates.endWeek') || 'End Week'} *
                  </label>
                  <input
                    type="number"
                    min={editFormData.start_week}
                    value={editFormData.end_week}
                    onChange={(e) => {
                      const week = Math.max(editFormData.start_week, parseInt(e.target.value) || editFormData.start_week);
                      setEditFormData({...editFormData, end_week: week});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setEditingInchstone(null)}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button onClick={handleSaveEdit} disabled={!editFormData.name.trim()}>
                {t('common.save') || 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectStructureTemplateBuilder;
